import type { Listing } from '@thevillages/schemas';

import { Component, computed, inject, input, signal } from '@angular/core';

import { SaveService } from '../../services/save/save';

@Component({
  imports: [],
  selector: 'app-save-listing',
  styleUrl: './save-listing.css',
  templateUrl: './save-listing.html',
})
export class SaveListing {
  private readonly _saveService = inject(SaveService);

  public readonly label = input(false);
  public readonly listing = input<Listing | undefined>();

  protected readonly buttonClass = computed(() => {
    return this.label()
      ? 'rounded-md text-sm focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white/30 dark:focus-visible:outline-white/30 flex flex-row gap-2 items-center justify-center cursor-pointer'
      : 'rounded-md text-sm focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white/30 dark:focus-visible:outline-white/30 flex flex-row gap-2 h-10 w-10 items-center justify-end cursor-pointer';
  });

  private readonly _pushChanges = signal(false);
  protected readonly saveSvg = computed(() => {
    this._pushChanges();
    return this.listing()?.savedItemId
      ? 'm480-120-58-52q-101-91-167-157T150-447.5Q111-500 95.5-544T80-634q0-94 63-157t157-63q52 0 99 22t81 62q34-40 81-62t99-22q94 0 157 63t63 157q0 46-15.5 90T810-447.5Q771-395 705-329T538-172l-58 52Z'
      : 'm480-120-58-52q-101-91-167-157T150-447.5Q111-500 95.5-544T80-634q0-94 63-157t157-63q52 0 99 22t81 62q34-40 81-62t99-22q94 0 157 63t63 157q0 46-15.5 90T810-447.5Q771-395 705-329T538-172l-58 52Zm0-108q96-86 158-147.5t98-107q36-45.5 50-81t14-70.5q0-60-40-100t-100-40q-47 0-87 26.5T518-680h-76q-15-41-55-67.5T300-774q-60 0-100 40t-40 100q0 35 14 70.5t50 81q36 45.5 98 107T480-228Zm0-273Z';
  });
  protected readonly svgClass = computed(() => {
    this._pushChanges();
    return this.listing()?.savedItemId
      ? 'fill-red-500 dark:fill-red-400 h-6 w-6'
      : 'fill-gray-400 dark:fill-gray-100 h-6 w-6';
  });

  private _saveDebounce: ReturnType<typeof setTimeout> | undefined = undefined;

  private async _performSave(): Promise<void> {
    if (this.listing()?.savedItemId) {
      const _deleteResult = await this._saveService.deleteSavedItem(
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- We know this is defined because we checked above.
        this.listing()!.savedItemId!,
        'listing',
      );
      if (_deleteResult) {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- We know this is defined because we checked above.
        this.listing()!.savedItemId = undefined;
        this._pushChanges.set(!this._pushChanges());
      }

      return;
    }

    if (this.listing()?.id) {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- We know this is defined because we checked above.
      const _saveResult = await this._saveService.saveListing(this.listing()!.id);

      if (_saveResult) {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- We know this is defined because we checked above.
        this.listing()!.savedItemId = _saveResult;
        this._pushChanges.set(!this._pushChanges());
      }
    }
  }

  protected saveListing(e: Event): void {
    e.stopPropagation();
    e.preventDefault();

    if (!this.listing()) {
      return;
    }

    clearTimeout(this._saveDebounce);
    this._saveDebounce = undefined;

    this._saveDebounce = setTimeout(() => {
      void this._performSave();
    }, 300);
  }
}
